/**
 * The input surface of a block — its "form", described as data.
 *
 * A block declares slots; a host (Regta's editor, Hyran's NRI form, a
 * third-party integration) renders them however it likes. The framework
 * itself never renders a form. This is the half of the old template model
 * that was always data-shaped, kept deliberately close to the existing
 * `VariableField` vocabulary so porting a template is a translation, not a
 * redesign.
 *
 * Option `code`s are frozen public API (ADR-0062): a label may be
 * re-worded, a code never changes within a block version.
 */
/** Slot types whose value is an option code (or a list of them). */
export const OPTION_SLOT_TYPES = ['select', 'multi_select', 'select_with_freetext'];
const findOption = (slot, raw) => {
    if (!slot?.options || typeof raw !== 'string')
        return undefined;
    return (slot.options.find((o) => o.code === raw) ??
        slot.options.find((o) => o.value === raw) ??
        slot.options.find((o) => o.label === raw));
};
/**
 * The option code for a stored answer — accepts a code, a legacy stored
 * value or a label (in that precedence). `undefined` when nothing matches
 * (e.g. the free text of a `select_with_freetext`).
 */
export function optionCodeFor(slot, raw) {
    return findOption(slot, raw)?.code;
}
/**
 * The legacy stored value for an answer (code, value or label accepted):
 * the option's `value`, falling back to its `label`. `undefined` when
 * nothing matches.
 */
export function optionValueFor(slot, code) {
    const option = findOption(slot, code);
    return option ? (option.value ?? option.label) : undefined;
}
const translateOptionValues = (slots, values, map) => {
    const out = { ...values };
    for (const slot of slots) {
        if (!slot.options?.length || !OPTION_SLOT_TYPES.includes(slot.type))
            continue;
        if (!Object.prototype.hasOwnProperty.call(values, slot.key))
            continue;
        const raw = values[slot.key];
        out[slot.key] = Array.isArray(raw) ? raw.map((r) => map(slot, r) ?? r) : (map(slot, raw) ?? raw);
    }
    return out;
};
/**
 * Rewrites every select answer in `values` to its option code (legacy
 * value / label → code). Unmatched answers (free text) pass through.
 */
export const toOptionCodes = (slots, values) => translateOptionValues(slots, values, optionCodeFor);
/** The inverse of `toOptionCodes`: every select answer → its legacy stored value. */
export const toOptionValues = (slots, values) => translateOptionValues(slots, values, optionValueFor);
const asArray = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);
const slotIndex = (slots) => {
    if (!slots)
        return undefined;
    if (slots instanceof Map)
        return slots;
    const index = new Map();
    for (const s of slots)
        if (!index.has(s.key))
            index.set(s.key, s);
    return index;
};
/**
 * Evaluates a `SlotCondition` against a flat value bag. Pure.
 *
 * Option rule: conditions are authored against option **codes**. When the
 * caller passes `slots` and the condition's key is a select slot, both the
 * stored answer and the condition's operand are canonicalised to the code
 * first, so a legacy stored value (or label) and its code compare equal.
 * Without `slots` the comparison is literal.
 */
export function evaluateCondition(condition, values, slots) {
    return evaluateIndexed(condition, values, slotIndex(slots));
}
function evaluateIndexed(condition, values, index) {
    if (!condition)
        return true;
    if ('all' in condition)
        return condition.all.every((c) => evaluateIndexed(c, values, index));
    if ('any' in condition)
        return condition.any.some((c) => evaluateIndexed(c, values, index));
    if ('not' in condition)
        return !evaluateIndexed(condition.not, values, index);
    const slot = index?.get(condition.key);
    const canon = slot?.options?.length && OPTION_SLOT_TYPES.includes(slot.type)
        ? (v) => optionCodeFor(slot, v) ?? v
        : (v) => v;
    const raw = values[condition.key];
    const value = Array.isArray(raw) ? raw.map(canon) : canon(raw);
    if ('equals' in condition)
        return value === canon(condition.equals);
    if ('notEquals' in condition)
        return value !== canon(condition.notEquals);
    if ('includes' in condition)
        return asArray(value).includes(canon(condition.includes));
    if ('oneOf' in condition)
        return condition.oneOf.map(canon).includes(value);
    return condition.isSet ? value != null && value !== '' : value == null || value === '';
}
/** The slots a host should currently show, given the values so far. */
export function visibleSlots(slots, values) {
    const index = slotIndex(slots);
    return slots.filter((s) => evaluateIndexed(s.visibleWhen, values, index));
}
/** Whether a slot is mandatory for these values: `required`, `statutory`, or `requiredWhen` holding. */
export function isSlotRequired(slot, values, slots) {
    if (slot.type === 'note')
        return false;
    if (slot.statutory || slot.required)
        return true;
    return slot.requiredWhen ? evaluateCondition(slot.requiredWhen, values, slots) : false;
}
/**
 * Visible slots that are mandatory but unfilled (`statutory`, `required`,
 * or `requiredWhen` holding). A host renders these as blocking errors; the
 * compiler surfaces them as diagnostics.
 */
export function missingStatutorySlots(slots, values) {
    const index = slotIndex(slots);
    return visibleSlots(slots, values).filter((s) => {
        if (!isSlotRequired(s, values, index))
            return false;
        const value = values[s.key];
        if (value == null || value === '')
            return true;
        return Array.isArray(value) && value.length === 0;
    });
}
//# sourceMappingURL=slots.js.map